import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { ActivityFileTypeEnum, FileTypeEnum } from '@sport-activities/sdk';
import { from, of, Subscription } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';
import { CtaButtonTypeEnum } from '~/component/call-to-action/enums/button-type.enum';

import { TipsVariantEnum } from '~/component/tips/enum/variant.enum';
import { CDN_ROOT } from '~/core/core.module';
import { SuccessStatusEnum } from '~/core/enums/success-status.enum';
import { base64toBlob } from '~/core/helpers/image/base64-to-blob/base64-to-blob.helper';
import { Activity } from '~/core/model/activity.model';
import { repeatUntil } from '~/core/operators/repeat-until.operator';
import { ActivityMediaResolutionEnum } from '~/organizer/shared/enums/media-resolution.enum';
import {
  getRatio,
  mediaResolutions,
} from '~/organizer/shared/helpers/media/media.helper';
import { OrganizerSharedActivityService } from '~/organizer/shared/services/activity/activity.service';

@Component({
  selector: 'app-organizer-activity-edition-photo',
  templateUrl: './photo.component.html',
  styleUrls: ['./photo.component.scss'],
})
export class OrganizerActivityEditionPhotoComponent
  implements OnInit, OnDestroy
{
  private readonly subscriptions = new Subscription();

  public readonly ctaButtonType = CtaButtonTypeEnum;
  public readonly variantTips = TipsVariantEnum.INFO;

  public activity: Activity;
  public isFormValid = false;
  public isOverlayDisplayed = false;
  public overlayStatus = SuccessStatusEnum.LOADING;
  public photo: FormControl;

  constructor(
    @Inject(CDN_ROOT) private readonly cdnRoot: string,
    private readonly activatedRoute: ActivatedRoute,
    private readonly organizerSharedActivityService: OrganizerSharedActivityService
  ) {}

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public ngOnInit(): void {
    this.photo = new FormControl('', Validators.required);

    this.subscriptions.add(
      this.activatedRoute.data
        .pipe(
          mergeMap(({ activityData }) => {
            return this.organizerSharedActivityService.getActivity(
              activityData.activity?.sku
            );
          })
        )
        .subscribe((activity) => {
          this.photo.setValue(
            activity.getMediaUrlByType(
              'MAIN_MEDIA' as ActivityFileTypeEnum,
              ActivityMediaResolutionEnum.LARGE
            ) || activity.image?.url
          );
          this.activity = activity;
        })
    );

    this.subscriptions.add(
      this.photo.valueChanges.subscribe(() => {
        this.checkFormValidity();
      })
    );
  }

  public get hasInitialValue(): boolean {
    return Boolean(
      this.activity?.getMediaUrlByType(
        'MAIN_MEDIA' as ActivityFileTypeEnum,
        ActivityMediaResolutionEnum.LARGE
      ) || this.activity?.picture
    );
  }

  public get imageAspectRatio(): number {
    return getRatio(ActivityMediaResolutionEnum.LARGE);
  }

  private getDefaultImageUrl(): string {
    return `${this.cdnRoot}/common/default-618x412.jpg`;
  }

  public checkFormValidity(): void {
    this.isFormValid = this.photo.valid && this.photo.dirty;
  }

  public updatePhoto(): void {
    this.isOverlayDisplayed = true;

    of(this.photo.value)
      .pipe(
        mergeMap((value: string) => {
          // Convert default image to blob
          if (value.startsWith('http')) {
            return from(fetch(value)).pipe(
              mergeMap((fetched) => from(fetched.blob()))
            );
          }

          return of(base64toBlob(value));
        }),
        mergeMap((blobToUpload) =>
          this.organizerSharedActivityService.addMedia(
            this.activity.sku,
            blobToUpload,
            `${this.activity.sku}_${uuidv4()}`,
            ['MAIN_MEDIA' as FileTypeEnum],
            [
              mediaResolutions[ActivityMediaResolutionEnum.LARGE],
              mediaResolutions[ActivityMediaResolutionEnum.CARD],
              mediaResolutions[ActivityMediaResolutionEnum.ICON],
            ]
          )
        ),
        mergeMap((uploadedMedia) => {
          return this.organizerSharedActivityService
            .getActivity(this.activity?.sku?.toString())
            .pipe(
              repeatUntil((activity) =>
                activity.medias.some(
                  (media) => media.identifier === uploadedMedia.identifier
                )
              )
            );
        })
      )
      .subscribe(() => {
        this.overlayStatus = SuccessStatusEnum.SUCCESS;
      });
  }

  public useDefaultPhoto(): void {
    this.photo.setValue(this.getDefaultImageUrl());
    this.photo.markAsDirty();

    this.checkFormValidity();
  }
}
